import { Box } from "@chakra-ui/react";
import { ProtectedImage } from "../ProtectedImage";
import { List, ListItem } from "../../api/lists";

// Per Figma node 3081:57042 (Series details detail pane). Natural sizes —
// three covers 109×168, staggered 22px right + 34px down per layer. Stack
// envelope is the union of all three slots.
const NATURAL_COVER_W = 109;
const NATURAL_COVER_H = 168;
const NATURAL_STAGGER_X = 22;
const NATURAL_STAGGER_Y = 34;
const NATURAL_SLOTS = [
  { x: 0, y: 0 },
  { x: NATURAL_STAGGER_X, y: NATURAL_STAGGER_Y },
  { x: NATURAL_STAGGER_X * 2, y: NATURAL_STAGGER_Y * 2 },
] as const;
const NATURAL_STACK_W =
  NATURAL_COVER_W + NATURAL_STAGGER_X * (NATURAL_SLOTS.length - 1); // 153
export const NATURAL_STACK_HEIGHT =
  NATURAL_COVER_H + NATURAL_STAGGER_Y * (NATURAL_SLOTS.length - 1); // 236

// Clamp scale so a tiny title column doesn't crush the covers, and a
// runaway-tall column doesn't blow them up beyond reason. The lower bound
// is permissive enough to cover the lists shelf rail row's smaller render
// while still preventing illegibly tiny stacks.
const MIN_SCALE = 0.5;
const MAX_SCALE = 1.4;

export interface ReadingListDetailHeroCoverStackProps {
  list: List;
  /**
   * Target height the stack ENVELOPE should fill, in px (i.e. the bounding
   * box around all three diagonally-staggered covers). Used by the detail
   * pane, where the stack is sized to match the title column's height.
   * Mutually exclusive with coverHeight; ignored when coverHeight is set.
   */
  height?: number;
  /**
   * Target height of an INDIVIDUAL cover in the stack, in px. Convenience
   * for callers whose design spec is in cover dimensions rather than the
   * stack envelope (e.g. the lists shelf rail row, where Figma specifies
   * "covers are 142×92" rather than a stack height). The component scales
   * every internal dimension proportionally to match.
   */
  coverHeight?: number;
}

function pickThumb(item: ListItem): string | undefined {
  // Releases: use `thumbnail_url`. That's the same field browse's
  // ReleaseGridCard and the search-v2 picker grid render — keeps the
  // detail hero visually consistent with every other release surface.
  // Note: backend's `release.image_*_url` is a separate "release cover
  // avatar" field that defaults to a "MISSING COVER IMAGE" placeholder
  // when the release has no custom one — we deliberately don't read it.
  if (item.release) {
    return item.release.thumbnail_url;
  }
  const ctx = item.comic;
  return ctx?.image_small_url ?? ctx?.image_url ?? ctx?.image_tiny_url;
}

export function ReadingListDetailHeroCoverStack({
  list,
  height,
  coverHeight,
}: ReadingListDetailHeroCoverStackProps) {
  // coverHeight wins over height — both are absolute pixel targets, but
  // coverHeight maps directly to the per-cover dimension that design
  // specs reference; height is a convenience for the detail pane case.
  const rawScale = coverHeight
    ? coverHeight / NATURAL_COVER_H
    : height
      ? height / NATURAL_STACK_HEIGHT
      : 1;
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, rawScale));
  const stackW = NATURAL_STACK_W * scale;
  const stackH = NATURAL_STACK_HEIGHT * scale;
  const coverW = NATURAL_COVER_W * scale;
  const coverH = NATURAL_COVER_H * scale;
  const slots = NATURAL_SLOTS.map((s) => ({
    x: s.x * scale,
    y: s.y * scale,
  }));

  const customCover = list.image_url ?? list.image_small_url;

  // Custom cover = single front-slot image. We still reserve the full stack
  // box so the surrounding layout doesn't shift between variants.
  if (customCover) {
    const slot = slots[slots.length - 1];
    return (
      <Box
        position="relative"
        // zIndex=0 establishes a local stacking context so the absolutely
        // positioned cover image inside doesn't escape into the document
        // root and render above the navbar (which sits at zIndex 2 in
        // the non-reader root layout — see RootLayout.tsx:235).
        zIndex={0}
        width={`${stackW}px`}
        height={`${stackH}px`}
        flexShrink={0}
        // Smooth resize when the title column changes size between selections.
        transition="width 200ms ease-out, height 200ms ease-out"
      >
        <ProtectedImage
          src={customCover}
          alt=""
          position="absolute"
          left={`${slot.x}px`}
          top={`${slot.y}px`}
          width={`${coverW}px`}
          height={`${coverH}px`}
          objectFit="cover"
          borderRadius="6px"
          border="1px solid"
          borderColor="transparent.white.10"
          boxShadow="6px 6px 18px rgba(0,0,0,0.55)"
        />
      </Box>
    );
  }

  // Cover URL list — fill from back-to-front (largest stagger renders on
  // top), gracefully shrinking the visible count when the list has fewer
  // thumbs than slots.
  //
  // Two input shapes:
  //   1. Listing endpoints (My Shelf rail) ship a flat `list.covers: string[]`
  //      (max 3 URLs, pre-resolved by the backend). Use that directly.
  //   2. Detail endpoint ships `list.list_items[]` with full release/comic
  //      blobs — derive the thumb URL the same way the legacy renderer did
  //      so the detail pane's stack stays visually identical.
  const flatCovers = list.covers ?? null;
  const visibleUrls: string[] = flatCovers
    ? flatCovers.slice(0, slots.length).filter((u): u is string => !!u)
    : (list.list_items ?? [])
        .map((item) => pickThumb(item))
        .filter((u): u is string => u != null)
        .slice(0, slots.length);

  // Single-item lists look lopsided when rendered into the back slot of a
  // 3-cover stack — the empty bottom-right half reads as missing content
  // rather than a deliberate stagger. Promote the lone cover to a centered,
  // slightly-enlarged hero that occupies the whole envelope instead.
  if (visibleUrls.length === 1) {
    const singleUrl = visibleUrls[0];
    // ~115% of natural cover height, capped so we never overflow the stack
    // envelope (which the surrounding layout has already reserved space
    // for). Preserve the cover's 65:100 aspect.
    const singleCoverH = Math.min(stackH, coverH * 1.15);
    const singleCoverW =
      singleCoverH * (NATURAL_COVER_W / NATURAL_COVER_H);
    const singleLeft = (stackW - singleCoverW) / 2;
    const singleTop = (stackH - singleCoverH) / 2;
    return (
      <Box
        position="relative"
        zIndex={0}
        width={`${stackW}px`}
        height={`${stackH}px`}
        flexShrink={0}
        transition="width 200ms ease-out, height 200ms ease-out"
      >
        <ProtectedImage
          src={singleUrl}
          alt=""
          position="absolute"
          left={`${singleLeft}px`}
          top={`${singleTop}px`}
          width={`${singleCoverW}px`}
          height={`${singleCoverH}px`}
          objectFit="cover"
          borderRadius="6px"
          border="1px solid"
          borderColor="transparent.white.10"
          // Same heavy shadow the front-most slot uses — anchors the cover
          // visually now that there's no stack behind it.
          boxShadow="6px 6px 18px rgba(0,0,0,0.55)"
          pointerEvents="none"
        />
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      // zIndex=0 establishes a local stacking context so the absolutely
      // positioned children's z-indexes (1/2/3 for back→front layering)
      // stay scoped INSIDE this stack instead of competing with the
      // navbar, which sits at zIndex 2 in the document root in non-reader
      // mode (see RootLayout.tsx:235). Without this, the front-most cover
      // (z=3) was rendering above the global search bar on scroll.
      zIndex={0}
      width={`${stackW}px`}
      height={`${stackH}px`}
      flexShrink={0}
      transition="width 200ms ease-out, height 200ms ease-out"
    >
      {visibleUrls.map((url, i) => {
        const slot = slots[i];
        return (
          <ProtectedImage
            key={`${i}-${url}`}
            src={url}
            alt=""
            position="absolute"
            left={`${slot.x}px`}
            top={`${slot.y}px`}
            width={`${coverW}px`}
            height={`${coverH}px`}
            objectFit="cover"
            borderRadius="6px"
            border="1px solid"
            borderColor="transparent.white.10"
            // Heavier shadow on the front-most cover (i = last) to anchor the
            // stack visually; back layers get a softer drop so they recede.
            boxShadow={
              i === visibleUrls.length - 1
                ? "6px 6px 18px rgba(0,0,0,0.55)"
                : "3px 4px 10px rgba(0,0,0,0.45)"
            }
            zIndex={i + 1}
            pointerEvents="none"
          />
        );
      })}
    </Box>
  );
}
